import { useCallback, useMemo } from "react";
import useAuth from "app/hooks/useAuth";
import { hasAccessForUser } from "app/utils/checkAccess";
import { PageNames } from "app/utils/pageNames";
import {
  canActorAssignInModule,
  canActorManageModule,
  resolveVisibleModuleKeys,
} from "@hms-operational/roleAdmin/moduleAdminRegistry";

const EMPTY_ROLES = [];

export function useModuleAdminAccess() {
  const { user } = useAuth();
  const actorRoles = useMemo(
    () => user?.roleList ?? EMPTY_ROLES,
    [user?.roleList],
  );

  const hasPageReadAccess = useMemo(
    () => hasAccessForUser(PageNames.MANAGE_USER_ROLES, "read", user),
    [user],
  );

  const hasPageWriteAccess = useMemo(
    () => hasAccessForUser(PageNames.MANAGE_USER_ROLES, "read/write", user),
    [user],
  );

  const visibleModuleKeys = useMemo(() => {
    if (!hasPageReadAccess) {
      return [];
    }
    return resolveVisibleModuleKeys(actorRoles);
  }, [actorRoles, hasPageReadAccess]);

  const visibleModuleKeysKey = visibleModuleKeys.join(",");

  const canManageModule = useCallback(
    (moduleKey) =>
      hasPageReadAccess && canActorManageModule(actorRoles, moduleKey),
    [actorRoles, hasPageReadAccess],
  );

  const canAssignInModule = useCallback(
    (moduleKey) =>
      hasPageWriteAccess && canActorAssignInModule(actorRoles, moduleKey),
    [actorRoles, hasPageWriteAccess],
  );

  return {
    actorRoles,
    visibleModuleKeys,
    visibleModuleKeysKey,
    canManageModule,
    canAssignInModule,
    hasPageReadAccess,
    hasPageWriteAccess,
    hasAnyModuleAccess: visibleModuleKeys.length > 0,
  };
}
